import React, { memo } from 'react';
import classNames from 'classnames';
import moment from 'moment';

import { Input } from 'src/components/helpers/Input';
import Textarea from 'src/components//helpers/Textarea';
import Checkbox from 'src/components/helpers/Checkbox';
import { IconButton } from 'src/components/helpers/IconButton';
import { unwrap } from 'src/utils/ChangeSpy';
import { dayToString } from 'src/utils/dateFormat';

import { FilesPanel } from './FilesPanel';
import { AgreementCategories } from './AgreementCategories';

import style from './style.module.css';

const RestartAgreementsComponent = ({
  files,
  isReadOnly,
  retainers,
  onChange,
  onDelete,
  onSelectAgreementCategories,
  onAgreementLink,
  onAgreementUnlink,
  onUploadFile,
  onDeleteFile,
}) => {
  const NAME_PREFIX = 'companyRetainers';

  return retainers.map((retainer, index) => {
    const id = retainer.get('id');
    const isSourceForAgreement = retainer.get('isSourceForAgreement');
    const isNew = typeof id === 'string';
    const agreementCategories = unwrap(retainer.get('agreementCategories'));
    const startDate = unwrap(retainer.get('startDate'));
    const restartAgreement = moment.isMoment(startDate) && startDate.isValid() ? `- ${dayToString(startDate)}` : null;
    const agreementFiles = files.filter(file => file.get('companyRetainerId') === id);
    const formClassName = classNames(style.item, {
      [style.newAgreement]: isNew,
    });

    if (unwrap(retainer.get('type') === 'retainer')) {
      return (
        <div key={id} className={formClassName} id={`agreementId_${id}`}>
          <div className={style.titleWrap}>
            <h5 className={style.title}>Restart Agreement {restartAgreement}</h5>
          </div>
          <IconButton
            className={style.deleteButton}
            disabled={isReadOnly}
            icon="remove"
            onClick={() => onDelete(id, isSourceForAgreement)}
            size="sm"
            title="Delete"
            variant="danger"
          />
          <div className="row">
            <div className="col-xs-12 col-sm-5 col-md-5">
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm retainer-contractual-input"
                      disabled={isReadOnly}
                      label="Contractual Name"
                      name={`${NAME_PREFIX}.${index}.contractualName`}
                      onChange={onChange}
                      value={retainer.get('contractualName')}
                      showLabel
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      disabled={isReadOnly}
                      label="Monthly Retainer"
                      name={`${NAME_PREFIX}.${index}.retainer`}
                      type="money"
                      value={retainer.get('retainer')}
                      showLabel
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="Start Date"
                      name={`${NAME_PREFIX}.${index}.startDate`}
                      onChange={onChange}
                      placeholder="Start Date"
                      title="This is the date, per the contract, that the project kicks off"
                      type="date"
                      value={retainer.get('startDate')}
                      showLabel
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="End Date"
                      name={`${NAME_PREFIX}.${index}.endDate`}
                      onChange={onChange}
                      placeholder="End Date"
                      title="This is the date the agreement officially terminates"
                      type="date"
                      value={retainer.get('endDate')}
                      showLabel
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="Notification to Start Date"
                      name={`${NAME_PREFIX}.${index}.notificationStartDate`}
                      onChange={onChange}
                      placeholder="Notification to Start Date"
                      title="This is the date we were informed of the client's intent to start"
                      type="date"
                      value={retainer.get('notificationStartDate')}
                      showLabel
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="Notification to Cancel Date"
                      name={`${NAME_PREFIX}.${index}.notificationCancellationDate`}
                      onChange={onChange}
                      placeholder="Notification to Cancel Date"
                      title="This is the date that the client informed us of their intent to cancel"
                      type="date"
                      value={retainer.get('notificationCancellationDate')}
                      showLabel
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <span className="field-label">Tail</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div className={classNames(style.tail, 'row')}>
                    <div className="col-xs-4">
                      <div className="form-group d-flex">
                        <Input
                          className={classNames(style.tailMonthsInput, 'input-sm')}
                          disabled={isReadOnly}
                          id={`retainer-tail-months-${index}`}
                          min="0"
                          name={`${NAME_PREFIX}.${index}.tailMonths`}
                          onChange={onChange}
                          type="number"
                          value={retainer.get('tailMonths')}
                        />
                        <label htmlFor={`retainer-tail-months-${index}`}>Months from</label>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <Checkbox
                        checked={retainer.get('tailLastIntroduced')}
                        disabled={isReadOnly}
                        id={`retainer-tail-last-introduced-${index}`}
                        label="Last Introduced"
                        name={`${NAME_PREFIX}.${index}.tailLastIntroduced`}
                        onChange={onChange}
                      />
                    </div>
                    <div className="col-xs-4">
                      <Checkbox
                        checked={retainer.get('tailTermination')}
                        disabled={isReadOnly}
                        id={`retainer-tail-termination-${index}`}
                        label="Termination"
                        name={`${NAME_PREFIX}.${index}.tailTermination`}
                        onChange={onChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <Textarea
                    disabled={isReadOnly}
                    label="Notes"
                    name={`${NAME_PREFIX}.${index}.retainerNote`}
                    onChange={onChange}
                    placeholder=""
                    rows="4"
                    value={retainer.get('retainerNote')}
                    showLabel
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 mt10">
                  {isNew ? (
                    <b>Please save the changes before uploading the files.</b>
                  ) : (
                    <FilesPanel
                      files={agreementFiles}
                      onDeleteFile={onDeleteFile}
                      onUploadFile={event => onUploadFile(event, id)}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-7 col-md-7">
              <AgreementCategories
                agreementCategories={agreementCategories}
                onAgreementLink={selectedAgreementCategory =>
                  onAgreementLink({ agreementCategories, type: 'companyRetainers', index, selectedAgreementCategory })
                }
                onAgreementUnlink={agreementCategories =>
                  onAgreementUnlink({ agreementCategories, type: 'companyRetainers', index })
                }
                onSelectAgreementCategories={() =>
                  onSelectAgreementCategories(agreementCategories, 'companyRetainers', index)
                }
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  });
};

/** Restart agreements component. */
export const RestartAgreements = memo(RestartAgreementsComponent);
